import { useState, useEffect, useCallback, useRef } from 'react';
import { createLogger } from "@/lib/logger";
const log = createLogger("elefa-call");
import { unlockAudioPlayback } from '@/lib/audio';
import { supabase } from '@/integrations/supabase/client';
import { useElefaRingtone } from '@/hooks/useElefaRingtone';
import { createManagedChannel } from '@/lib/realtime-reconnect';
import { showIncomingCallNotification, showMissedCallNotification, requestNotificationPermission } from '@/lib/call-notifications';
import { CallQualityMonitor, type CallQuality, type CallQualityStats } from '@/lib/call-quality-monitor';
import { toast } from 'sonner';
import {
  deriveSharedSecret,
  encryptMessage,
  decryptMessage,
  getStoredPrivateKey,
} from '@/lib/encryption';
import { insertCallEventMessage, loadEarlyIceCandidates, type CallEventPayload } from '@/lib/call-events';

// ─── Module-level globals ───────────────────────────────────────────────────

declare global {
  interface Window {
    __elefaCallNotifDismiss?: (() => void) | null;
  }
  interface Navigator {
    connection?: {
      effectiveType?: string;
      addEventListener?: (event: string, cb: () => void) => void;
      removeEventListener?: (event: string, cb: () => void) => void;
    };
  }
}

type SinkIdAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
type ElefaCallRow = {
  id: string;
  status: string;
  caller_id?: string | null;
  callee_id?: string | null;
  is_video?: boolean | null;
  user_id?: string | null;
  [key: string]: unknown;
};

function errMessage(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) return { message: err.message, stack: err.stack };
  if (typeof err === 'object' && err !== null) {
    const e = err as { message?: string; stack?: string };
    return { message: e.message ?? String(err), stack: e.stack };
  }
  return { message: String(err) };
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface ElefaCallState {
  callId: string | null;
  callerId: string | null;
  calleeId: string | null;
  callerName: string | null;
  callerAvatar: string | null;
  status: 'idle' | 'ringing' | 'connecting' | 'connected' | 'ended';
  isVideo: boolean;
  isIncoming: boolean;
  duration: number;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
}

export interface UseElefaCallReturn extends ElefaCallState {
  initiateCall: (userId: string, isVideo?: boolean) => Promise<void>;
  answerCall: (callId: string) => Promise<void>;
  rejectCall: (callId: string) => Promise<void>;
  endCall: () => Promise<void>;
  toggleMute: () => void;
  toggleVideo: () => void;
  isMuted: boolean;
  isVideoEnabled: boolean;
  /** Real-time call quality (Signal-grade monitoring) */
  callQuality: CallQuality | null;
  /** True when ICE is disconnected and restarting */
  isReconnecting: boolean;
  /** True when sharing screen */
  isScreenSharing: boolean;
  /** Toggle screen sharing on/off */
  toggleScreenShare: () => void;
  /** Speaker/speakerphone toggle */
  isSpeakerOn: boolean;
  toggleSpeaker: () => void;
  /** Flip camera (front/rear) for video calls */
  flipCamera: () => Promise<void>;
  /** Hold/Resume - CallKit-compatible */
  isOnHold: boolean;
  /** True when the remote party has put us on hold */
  isRemoteOnHold: boolean;
  toggleHold: () => void;
}

const IDLE_STATE: ElefaCallState = {
  callId: null,
  callerId: null,
  calleeId: null,
  callerName: null,
  callerAvatar: null,
  status: 'idle',
  isVideo: false,
  isIncoming: false,
  duration: 0,
  localStream: null,
  remoteStream: null,
};

// ─── TURN credential fetching ───────────────────────────────────────────────

async function fetchTurnCredentials(): Promise<RTCConfiguration> {
  try {
    const { data, error } = await supabase.functions.invoke('elefa-turn-credentials');
    if (error) throw error;
    
    if (data?.iceServers) {
      return {
        iceServers: data.iceServers,
        iceCandidatePoolSize: 4,          // Pre-gather candidates for faster connectivity
        bundlePolicy: 'max-bundle',       // Multiplex audio+video on single port
        rtcpMuxPolicy: 'require',         // Reduce port usage, faster DTLS
      };
    }
  } catch (err) {
    log.warn('[ElefaCall] Failed to fetch TURN credentials, using STUN fallback:', err);
  }
  
  // Fallback to STUN-only
  return {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
    iceCandidatePoolSize: 4,
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
  };
}

// ─── Audio unlock helper ────────────────────────────────────────────────────
// Must be called synchronously within a user gesture (click/tap) to unlock
// unlockAudioPlayback is now imported from @/lib/audio

// ─── Codec preference helpers ───────────────────────────────────────────────
// Prefer Opus for audio (Signal uses Opus exclusively) and VP9/VP8 for video.

function preferOpusCodec(description: RTCSessionDescriptionInit): RTCSessionDescriptionInit {
  if (!description.sdp) return description;

  // Boost Opus bitrate to 48kbps for HD voice (Signal uses 40-64kbps)
  // Enable FEC (forward error correction) and DTX (discontinuous transmission)
  let sdp = description.sdp;

  // Add Opus parameters for all Opus payload types
  sdp = sdp.replace(
    /a=fmtp:111 /g,
    'a=fmtp:111 maxaveragebitrate=48000;stereo=0;sprop-stereo=0;useinbandfec=1;usedtx=1;'
  );

  // If the above didn't match (different payload type), try a more generic approach
  if (!sdp.includes('maxaveragebitrate=48000')) {
    sdp = sdp.replace(
      /(a=rtpmap:\d+ opus\/48000\/2\r?\n)/g,
      '$1a=fmtp:111 minptime=10;useinbandfec=1;usedtx=1;maxaveragebitrate=48000\r\n'
    );
  }

  return { ...description, sdp };
}

// ─── Adaptive bitrate control (Signal-style) ────────────────────────────────
// After connection, set sender parameters for optimal audio/video quality.

function applyBitrateConstraints(pc: RTCPeerConnection) {
  try {
    const senders = pc.getSenders();
    for (const sender of senders) {
      if (!sender.track) continue;
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }

      if (sender.track.kind === 'audio') {
        // Opus at 48kbps - matches Signal's HD voice quality
        params.encodings[0].maxBitrate = 48000;
        // Enable network adaptation priority
        params.encodings[0].networkPriority = 'high' as RTCPriorityType;
        params.encodings[0].priority = 'high';
      } else if (sender.track.kind === 'video') {
        // Cap video at 1.5 Mbps for 720p - prevents audio starvation
        params.encodings[0].maxBitrate = 1_500_000;
        params.encodings[0].networkPriority = 'low' as RTCPriorityType;
        params.encodings[0].priority = 'low';
      }

      sender.setParameters(params).catch(() => {
        // Some browsers don't support all parameters
      });
    }
    log.debug('[ElefaCall] Bitrate constraints applied - audio prioritized');
  } catch (err) {
    log.warn('[ElefaCall] Failed to apply bitrate constraints:', err);
  }
}

// ─── E2EE helpers ───────────────────────────────────────────────────────────

async function getSharedSecretForUser(otherUserId: string): Promise<CryptoKey | null> {
  try {
    const myPrivateKey = await getStoredPrivateKey();
    if (!myPrivateKey) {
      log.error('[ElefaCall] E2EE FAIL: Local private key unavailable from IndexedDB');
      toast.error('Encryption key not ready. Please wait a moment and retry.');
      return null;
    }

    const { data, error } = await supabase
      .from('encryption_keys')
      .select('public_key')
      .eq('user_id', otherUserId)
      .single();

    if (error || !data?.public_key) {
      log.error('[ElefaCall] E2EE FAIL: Remote public key fetch failed', error);
      toast.error('Could not retrieve contact encryption key.');
      return null;
    }

    return await deriveSharedSecret(myPrivateKey, data.public_key);
  } catch (err) {
    log.error('[ElefaCall] E2EE FAIL: Key derivation threw', err);
    toast.error('Encryption error. Please try again.');
    return null;
  }
}

async function encryptSignaling(content: string, sharedSecret: CryptoKey | null): Promise<string> {
  if (!sharedSecret) return content;
  try {
    return await encryptMessage(content, sharedSecret);
  } catch {
    return content;
  }
}

async function decryptSignaling(content: string, sharedSecret: CryptoKey | null): Promise<string> {
  if (!sharedSecret) return content;
  try {
    return await decryptMessage(content, sharedSecret);
  } catch {
    return content;
  }
}

// ─── Insert call event into chat thread ─────────────────────────────────────
// Signal-style: missed/rejected/completed calls appear as messages in chat.
// Idempotency: uses call_id in metadata to prevent duplicates.

// insertCallEventMessage and loadEarlyIceCandidates are now imported from '@/lib/call-events'

// ─── Main hook ──────────────────────────────────────────────────────────────

export function useElefaCall(): UseElefaCallReturn {
  const [state, setState] = useState<ElefaCallState>(IDLE_STATE);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isRingtonePlaying, setIsRingtonePlaying] = useState(false);
  const [callQuality, setCallQuality] = useState<CallQuality | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const remoteHoldRef = useRef(false);
  const [isRemoteOnHold, setIsRemoteOnHold] = useState(false);

  // Ref-based stream tracking for reliable cleanup (avoids stale closures)
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const failedGraceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const iceChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const sharedSecretRef = useRef<CryptoKey | null>(null);
  const isCleaningUpRef = useRef(false);
  const isInitiatingRef = useRef(false);
  // C7: Store key_version at call start for identity verification binding
  const callStartKeyVersionRef = useRef<number | null>(null);
  const qualityMonitorRef = useRef<CallQualityMonitor | null>(null);
  const screenTrackRef = useRef<MediaStreamTrack | null>(null);
  const originalVideoTrackRef = useRef<MediaStreamTrack | null>(null);

  // Stable refs for state access in callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  useElefaRingtone({ isPlaying: isRingtonePlaying, volume: 0.35 });

  // ─── Media ──────────────────────────────────────────────────────────────

  const getUserMedia = useCallback(async (isVideo: boolean): Promise<MediaStream | null> => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: {
          // Signal-grade audio processing
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // HD voice - request wideband (48kHz) for Opus
          sampleRate: 48000,
          sampleSize: 16,
          channelCount: 1,
        },
        video: isVideo
          ? {
              facingMode: 'user',
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30, max: 30 },
            }
          : false,
      });
    } catch (err) {
      log.error('[ElefaCall] getUserMedia failed:', err);
      return null;
    }
  }, []);

  // ─── Cleanup / End call ─────────────────────────────────────────────────

  const endCallInternal = useCallback(async (reason?: string) => {
    if (isCleaningUpRef.current) return;
    isCleaningUpRef.current = true;

    if (reason) log.debug('[ElefaCall] Ending call:', reason);

    const currentState = stateRef.current;

    // Clear ICE failed grace timeout
    if (failedGraceTimeoutRef.current) {
      clearTimeout(failedGraceTimeoutRef.current);
      failedGraceTimeoutRef.current = null;
    }

    // Stop ALL media tracks via refs (avoids stale state closures)
    const stopStream = (stream: MediaStream | null) => {
      stream?.getTracks().forEach(t => {
        t.stop();
        t.enabled = false;
      });
    };
    stopStream(localStreamRef.current);
    stopStream(remoteStreamRef.current);
    stopStream(currentState.localStream);
    stopStream(currentState.remoteStream);
    localStreamRef.current = null;
    remoteStreamRef.current = null;

    // Stop original video track (may be orphaned by screen share swap)
    if (originalVideoTrackRef.current) {
      originalVideoTrackRef.current.stop();
      originalVideoTrackRef.current = null;
    }

    // Stop quality monitor
    qualityMonitorRef.current?.stop();
    qualityMonitorRef.current = null;
    setCallQuality(null);
    setIsReconnecting(false);

    // Stop screen sharing
    screenTrackRef.current?.stop();
    screenTrackRef.current = null;
    originalVideoTrackRef.current = null;
    setIsScreenSharing(false);

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.onicecandidate = null;
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.onconnectionstatechange = null;
      peerConnectionRef.current.oniceconnectionstatechange = null;
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Remove ICE Realtime channel
    if (iceChannelRef.current) {
      supabase.removeChannel(iceChannelRef.current);
      iceChannelRef.current = null;
    }

    // Stop duration timer
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    // Stop heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    setIsRingtonePlaying(false);
    setIsSpeakerOn(false);
    sharedSecretRef.current = null;

    // O1: Store end_reason in DB + reset key version ref
    callStartKeyVersionRef.current = null;

    // Update call record in DB - respect existing terminal status
    if (currentState.callId) {
      // Read the current DB status first to avoid overwriting rejected/missed
      const { data: existingCall } = await supabase
        .from('elefa_calls')
        .select('status')
        .eq('id', currentState.callId)
        .single();

      const dbStatus = existingCall?.status;
      const isAlreadyTerminal = dbStatus === 'rejected' || dbStatus === 'missed' || dbStatus === 'ended';

      if (!isAlreadyTerminal) {
        // Determine correct final status:
        // If caller hangs up while still ringing (never connected), it's a missed call
        const wasNeverConnected = currentState.duration === 0 && currentState.status === 'ringing';
        const finalStatus = wasNeverConnected ? 'missed' : 'ended';

        await supabase
          .from('elefa_calls')
          .update({
            status: finalStatus,
            ended_at: new Date().toISOString(),
            duration_seconds: currentState.duration,
            end_reason: reason || 'user_hangup',
          })
          .eq('id', currentState.callId);
      }

      // Insert call event message into chat thread
      // Caller inserts for completed/missed calls; callee inserts for rejected (handled in rejectCall)
      const { data: { session: sess } } = await supabase.auth.getSession();
      const myId = sess?.user?.id;
      if (myId && currentState.callerId && currentState.calleeId) {
        const isCaller = myId === currentState.callerId;
        // Use the actual final status from DB (may have been set by callee)
        const actualStatus = isAlreadyTerminal ? dbStatus : (currentState.duration === 0 && currentState.status === 'ringing' ? 'missed' : 'ended');
        const isMissed = actualStatus === 'missed' || actualStatus === 'rejected';

        // Caller inserts event for: completed calls, and missed calls (caller hung up during ringing)
        // Callee already inserts for: rejected calls (in rejectCall) and missed timeout (in auto-timeout)
        if (isCaller && actualStatus !== 'rejected') {
          await insertCallEventMessage(myId, currentState.callerId, currentState.calleeId, {
            call_id: currentState.callId,
            is_video: currentState.isVideo,
            is_missed: isMissed,
            duration: currentState.duration,
            status: isMissed ? 'missed' : 'completed',
          });
        }
      }
    }

    setState(IDLE_STATE);
    setIsMuted(false);
    setIsVideoEnabled(true);
    isCleaningUpRef.current = false;
    isInitiatingRef.current = false; // Reset initiation guard on cleanup
  }, []);

  // ─── ICE candidate exchange via Realtime ────────────────────────────────

  const setupIceTrickle = useCallback((callId: string, userId: string) => {
    // Subscribe to ICE candidates from the other party
    const channel = supabase
      .channel(`ice-${callId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'elefa_call_ice_candidates',
          filter: `call_id=eq.${callId}`,
        },
        async (payload) => {
          const row = payload.new as { sender_id: string; candidate: string };
          
          // Only process candidates from the other party
          if (row.sender_id === userId) return;

          const pc = peerConnectionRef.current;
          if (!pc || pc.connectionState === 'closed') return;

          try {
            const decrypted = await decryptSignaling(row.candidate, sharedSecretRef.current);
            const candidate = JSON.parse(decrypted);
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (err) {
            log.warn('[ElefaCall] Failed to add remote ICE candidate:', err);
          }
        }
      )
      .subscribe();

    iceChannelRef.current = channel;
  }, []);

  const sendIceCandidate = useCallback(async (callId: string, userId: string, candidate: RTCIceCandidate) => {
    try {
      const encrypted = await encryptSignaling(
        JSON.stringify(candidate.toJSON()),
        sharedSecretRef.current
      );

      const { error } = await supabase.from('elefa_call_ice_candidates').insert({
        call_id: callId,
        sender_id: userId,
        candidate: encrypted,
      });

      if (error) {
        log.error('[ElefaCall] Failed to insert ICE candidate:', error.message);
      } else {
        log.debug('[ElefaCall] ICE candidate sent for call:', callId);
      }
    } catch (err) {
      log.warn('[ElefaCall] Failed to send ICE candidate:', err);
    }
  }, []);

  // ─── Peer connection factory (SIP-ready architecture) ───────────────────
  // The peer connection is created with a clean separation between
  // ICE/DTLS transport and SDP handling. When migrating to SIP,
  // replace the SDP source (offer/answer) with SIP INVITE/200 OK
  // while keeping the same ICE trickle and TURN infrastructure.

  const createPeerConnection = useCallback(async (
    callId: string,
    userId: string,
    iceConfig: RTCConfiguration
  ): Promise<RTCPeerConnection> => {
    const pc = new RTCPeerConnection(iceConfig);

    // ICE candidate handler - trickle via DB
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        log.debug('[ElefaCall] Local ICE candidate generated:', event.candidate.type, event.candidate.protocol);
        sendIceCandidate(callId, userId, event.candidate);
      } else {
        log.debug('[ElefaCall] ICE gathering complete');
      }
    };

    // Remote media - ensure audio tracks are enabled immediately
    pc.ontrack = (event) => {
      log.debug('[ElefaCall] Remote track received:', event.track.kind);
      // Ensure track is enabled (Signal-style immediate playback)
      event.track.enabled = true;
      remoteStreamRef.current = event.streams[0];
      setState(prev => ({ ...prev, remoteStream: event.streams[0] }));
    };

    // Connection state management
    pc.onconnectionstatechange = () => {
      log.debug('[ElefaCall] Connection state:', pc.connectionState);
      if (!peerConnectionRef.current) return;

      switch (pc.connectionState) {
        case 'connected':
          setState(prev => ({ ...prev, status: 'connected' }));
          setIsRingtonePlaying(false);
          setIsReconnecting(false);
          // Clear any ICE failed grace timeout on successful recovery
          if (failedGraceTimeoutRef.current) {
            clearTimeout(failedGraceTimeoutRef.current);
            failedGraceTimeoutRef.current = null;
          }
          // ── C7: IDENTITY VERIFICATION BINDING: Compare key_version against call start ──
          (async () => {
            try {
              const currentState = stateRef.current;
              const otherUserId = currentState.isIncoming ? currentState.callerId : currentState.calleeId;
              if (!otherUserId) return;
              const { data: keyRecord } = await supabase
                .from('encryption_keys')
                .select('public_key, key_version')
                .eq('user_id', otherUserId)
                .single();
              if (!keyRecord?.public_key) {
                log.error('[ElefaCall] SECURITY: No identity key for remote peer - terminating');
                endCallInternal('Remote identity key missing');
                return;
              }
              // C7 FIX: Compare current key_version against the version at call initiation
              const startVersion = callStartKeyVersionRef.current;
              if (startVersion !== null && keyRecord.key_version !== startVersion) {
                log.error('[ElefaCall] SECURITY: Identity key changed since call start (was', startVersion, 'now', keyRecord.key_version, ') - terminating');
                supabase.functions.invoke('elefa-security-log', {
                  body: { event_type: 'CALL_IDENTITY_MISMATCH_DTLS', severity: 'critical',
                    resource_type: 'call', resource_id: currentState.callId, action: 'identity_mismatch',
                    details: { other_user: otherUserId, start_version: startVersion, current_version: keyRecord.key_version } },
                }).catch(() => {});
                endCallInternal('Identity key changed during call setup');
                return;
              }
              log.debug('[ElefaCall] Remote identity verified: key_version =', keyRecord.key_version);
            } catch (err) {
              log.warn('[ElefaCall] Identity verification check failed:', err);
            }
          })();
          // Start duration counter
          if (!durationTimerRef.current) {
            durationTimerRef.current = setInterval(() => {
              setState(prev => ({ ...prev, duration: prev.duration + 1 }));
            }, 1000);
          }
          // Apply adaptive bitrate limits for audio quality
          applyBitrateConstraints(pc);
          break;
        case 'disconnected':
          setIsReconnecting(true);
          try {
            pc.restartIce();
          } catch { /* ignore */ }
          break;
        case 'failed':
          log.warn('[ElefaCall] Connection FAILED - attempting ICE restart with 5s grace');
          setIsReconnecting(true);
          try { pc.restartIce(); } catch { /* ignore */ }
          // Clear any existing grace timeout
          if (failedGraceTimeoutRef.current) clearTimeout(failedGraceTimeoutRef.current);
          failedGraceTimeoutRef.current = setTimeout(() => {
            if (peerConnectionRef.current?.connectionState === 'failed') {
              log.error('[ElefaCall] Connection still FAILED after grace period - ending call');
              setIsReconnecting(false);
              endCallInternal('Connection failed after retry');
            }
          }, 5000);
          break;
      }
    };

    pc.oniceconnectionstatechange = () => {
      log.debug('[ElefaCall] ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setIsReconnecting(false);
      } else if (pc.iceConnectionState === 'disconnected') {
        setIsReconnecting(true);
      }
      if (pc.iceConnectionState === 'failed') {
        try { pc.restartIce(); } catch { /* ignore */ }
      }
    };

    pc.onicegatheringstatechange = () => {
      log.debug('[ElefaCall] ICE gathering state:', pc.iceGatheringState);
    };

    peerConnectionRef.current = pc;
    return pc;
  }, [sendIceCandidate, endCallInternal]);

  // ─── Initiate call ──────────────────────────────────────────────────────

  const initiateCall = useCallback(async (targetUserId: string, isVideo = false) => {
    log.debug('[ElefaCall] initiateCall invoked for', targetUserId, 'video:', isVideo);
    // CRITICAL: Unlock audio playback within user gesture BEFORE any async ops
    unlockAudioPlayback();

    // Guard against duplicate calls
    if (isInitiatingRef.current) {
      log.warn('[ElefaCall] initiateCall already in progress, ignoring duplicate');
      return;
    }
    if (stateRef.current.status !== 'idle') {
      log.warn('[ElefaCall] Cannot initiate call while status is:', stateRef.current.status);
      return;
    }

    isInitiatingRef.current = true;

    try {
      // Use getSession (cached, no network) to avoid latency race under concurrent calls
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) { toast.error('Please sign in to make calls'); isInitiatingRef.current = false; return; }

      // ── AUTHORIZATION CHECK: Verify connection or shared room ──
      // Prevent calling users without an accepted connection (Signal zero-trust)
      const { data: canCall } = await supabase.rpc('can_call_user', {
        p_caller_id: user.id,
        p_callee_id: targetUserId,
      });
      if (!canCall) {
        log.error('[ElefaCall] BLOCKED: No accepted connection with target user');
        toast.error('You must be connected to this user before calling');
        // Log security event
        supabase.functions.invoke('elefa-security-log', {
          body: { event_type: 'CALL_UNAUTHORIZED', severity: 'warning',
            resource_type: 'call', action: 'initiate_blocked',
            details: { callee_id: targetUserId } },
        }).catch(() => {});
        isInitiatingRef.current = false;
        return;
      }

      // ── STALE CALL CLEANUP: Auto-end zombie calls older than 2 minutes ──
      const twoMinAgo = new Date(Date.now() - 2 * 60_000).toISOString();
      await supabase
        .from('elefa_calls')
        .update({ status: 'missed', ended_at: new Date().toISOString(), end_reason: 'stale_cleanup' })
        .or(`caller_id.eq.${user.id},callee_id.eq.${user.id}`)
        .eq('status', 'ringing')
        .lt('created_at', twoMinAgo);

      // ── CONCURRENT CALL CHECK: Prevent multi-device conflicts ──
      // Check BOTH caller and callee roles to catch all active calls
      const { data: activeCallerCalls } = await supabase
        .from('elefa_calls')
        .select('id')
        .eq('caller_id', user.id)
        .in('status', ['ringing', 'connecting', 'connected'])
        .limit(1);
      const { data: activeCalleeCalls } = await supabase
        .from('elefa_calls')
        .select('id')
        .eq('callee_id', user.id)
        .in('status', ['ringing', 'connecting', 'connected'])
        .limit(1);
      const hasActiveCalls = (activeCallerCalls && activeCallerCalls.length > 0) || 
                             (activeCalleeCalls && activeCalleeCalls.length > 0);
      if (hasActiveCalls) {
        log.warn('[ElefaCall] Already in an active call');
        toast.error('You already have an active call');
        isInitiatingRef.current = false;
        return;
      }

      // Derive E2EE shared secret + C7: capture key_version at call start
      const { data: targetKeyRecord } = await supabase
        .from('encryption_keys')
        .select('key_version')
        .eq('user_id', targetUserId)
        .single();
      callStartKeyVersionRef.current = targetKeyRecord?.key_version ?? null;
      sharedSecretRef.current = await getSharedSecretForUser(targetUserId);
      // Single retry after 1s for transient IndexedDB / network failures
      if (!sharedSecretRef.current) {
        await new Promise(r => setTimeout(r, 1000));
        sharedSecretRef.current = await getSharedSecretForUser(targetUserId);
      }
      // ── E2EE ENFORCEMENT: Block call if shared secret derivation fails ──
      if (!sharedSecretRef.current) {
        log.error('[ElefaCall] BLOCKED: E2EE key derivation failed after retry');
        toast.error('Encryption setup failed. Please try again.');
        isInitiatingRef.current = false;
        return;
      }

      // Get callee profile
      const { data: calleeProfile } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('user_id', targetUserId)
        .single();

      // Get local media
      const localStream = await getUserMedia(isVideo);
      if (!localStream) {
        log.error('[ElefaCall] No local media');
        toast.error('Microphone access is required to make calls');
        isInitiatingRef.current = false;
        return;
      }
      localStreamRef.current = localStream;

      // Fetch TURN credentials
      const iceConfig = await fetchTurnCredentials();
      log.debug('[ElefaCall] ICE config:', JSON.stringify(iceConfig.iceServers?.map(s => typeof s === 'string' ? s : s.urls)));

      // Create call record
      const { data: call, error } = await supabase
        .from('elefa_calls')
        .insert({
          caller_id: user.id,
          callee_id: targetUserId,
          status: 'ringing',
          is_video: isVideo,
        })
        .select()
        .single();

      if (error || !call) {
        log.error('[ElefaCall] Failed to create call:', error?.message, error?.code, error?.details, error?.hint);
        // RLS policy failure means can_call_user returned false
        if (error?.code === '42501' || error?.message?.includes('policy')) {
          toast.error('Unable to call this user. Ensure you are connected.');
        } else if (error?.code === '23505') {
          toast.error('A call is already in progress. Please end it first.');
        } else if (error?.code === '23503') {
          toast.error('This user account is no longer available.');
        } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
          toast.error('Network error. Check your connection and try again.');
        } else {
          toast.error(`Failed to start call: ${error?.message || 'Unknown error'}. Please try again.`);
        }
        localStream.getTracks().forEach(t => t.stop());
        isInitiatingRef.current = false;
        return;
      }

      log.debug('[ElefaCall] Call created:', call.id);

      setState({
        callId: call.id,
        callerId: user.id,
        calleeId: targetUserId,
        callerName: calleeProfile?.full_name || 'Unknown',
        callerAvatar: calleeProfile?.avatar_url || null,
        status: 'ringing',
        isVideo,
        isIncoming: false,
        duration: 0,
        localStream,
        remoteStream: null,
      });

      // Setup ICE trickle channel
      setupIceTrickle(call.id, user.id);

      // Create peer connection
      const pc = await createPeerConnection(call.id, user.id, iceConfig);

      // Add local tracks
      localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

      // Create offer with Opus HD voice optimization
      const rawOffer = await pc.createOffer();
      const offer = preferOpusCodec(rawOffer);
      await pc.setLocalDescription(offer);

      // Encrypt and store offer SDP
      const encryptedOffer = await encryptSignaling(
        JSON.stringify(offer),
        sharedSecretRef.current
      );

      await supabase
        .from('elefa_calls')
        .update({ offer_sdp: encryptedOffer })
        .eq('id', call.id);

      // Push-notify callee's device(s) - fire-and-forget so call proceeds
      const callerDisplayName = user.user_metadata?.full_name || user.email || 'Unknown';
      supabase.functions.invoke('push-notify-call', {
        body: {
          callee_id: targetUserId,
          call_id: call.id,
          is_video: isVideo,
          caller_name: callerDisplayName,
        },
      }).catch(err => log.warn('[ElefaCall] Push notify failed:', err));

      setIsRingtonePlaying(true);
    } catch (err: unknown) {
      const { message: errMsg, stack } = errMessage(err);
      log.error('[ElefaCall] initiateCall failed:', errMsg, stack);
      // Provide specific feedback based on failure type
      if (errMsg.includes('getUserMedia') || errMsg.includes('NotAllowed') || errMsg.includes('Permission')) {
        toast.error('Microphone access denied. Please allow microphone permissions.');
      } else if (errMsg.includes('ICE') || errMsg.includes('TURN') || errMsg.includes('credential')) {
        toast.error('Connection setup failed. Please check your network and try again.');
      } else if (errMsg.includes('encrypt') || errMsg.includes('E2EE') || errMsg.includes('shared secret')) {
        toast.error('Encryption setup failed. Please try again.');
      } else if (errMsg.includes('network') || errMsg.includes('fetch') || errMsg.includes('Failed to fetch')) {
        toast.error('Network error. Check your connection and try again.');
      } else {
        toast.error(`Call failed: ${errMsg.length > 80 ? errMsg.slice(0, 80) + '…' : errMsg}`);
      }
    } finally {
      isInitiatingRef.current = false;
    }
  }, [getUserMedia, createPeerConnection, setupIceTrickle]);

  // ─── Answer call ────────────────────────────────────────────────────────

  const answerCall = useCallback(async (callId: string) => {
    // CRITICAL: Unlock audio playback within user gesture BEFORE any async ops
    unlockAudioPlayback();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Get call details
      const { data: call } = await supabase
        .from('elefa_calls')
        .select('*')
        .eq('id', callId)
        .single();

      if (!call) return;

      // ── CALLEE IDENTITY CHECK: Verify we are the intended callee ──
      if (call.callee_id !== user.id) {
        log.error('[ElefaCall] SECURITY: callee_id mismatch - rejecting call');
        supabase.functions.invoke('elefa-security-log', {
          body: { event_type: 'CALL_IDENTITY_MISMATCH', severity: 'critical',
            resource_type: 'call', resource_id: callId, action: 'answer_rejected',
            details: { expected: call.callee_id, actual: user.id } },
        }).catch(() => {});
        return;
      }

      setIsRingtonePlaying(false);
      // Dismiss notification
      window.__elefaCallNotifDismiss?.();
      window.__elefaCallNotifDismiss = null;

      // Derive E2EE shared secret with caller + C7: capture key_version at answer time
      const { data: callerKeyRecord } = await supabase
        .from('encryption_keys')
        .select('key_version')
        .eq('user_id', call.caller_id)
        .single();
      callStartKeyVersionRef.current = callerKeyRecord?.key_version ?? null;
      sharedSecretRef.current = await getSharedSecretForUser(call.caller_id);
      // Single retry after 1s for transient failures
      if (!sharedSecretRef.current) {
        await new Promise(r => setTimeout(r, 1000));
        sharedSecretRef.current = await getSharedSecretForUser(call.caller_id);
      }
      // ── E2EE ENFORCEMENT: Block answer if shared secret derivation fails ──
      if (!sharedSecretRef.current) {
        log.error('[ElefaCall] BLOCKED: E2EE key derivation failed for caller after retry');
        return;
      }

      // Get local media
      const localStream = await getUserMedia(call.is_video);
      if (!localStream) return;
      localStreamRef.current = localStream;

      setState(prev => ({ ...prev, status: 'connecting', localStream }));

      // Fetch TURN credentials
      const iceConfig = await fetchTurnCredentials();

      // Setup ICE trickle channel
      setupIceTrickle(callId, user.id);

      // Create peer connection
      const pc = await createPeerConnection(callId, user.id, iceConfig);

      // Add local tracks
      localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

      // Decrypt and set remote offer
      if (call.offer_sdp) {
        const decryptedOffer = await decryptSignaling(call.offer_sdp, sharedSecretRef.current);
        const offer = JSON.parse(decryptedOffer);
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
      }

      // Also load any ICE candidates that arrived before we subscribed
      const { data: earlyCandidates } = await supabase
        .from('elefa_call_ice_candidates')
        .select('candidate, sender_id')
        .eq('call_id', callId)
        .neq('sender_id', user.id);

      if (earlyCandidates) {
        for (const row of earlyCandidates) {
          try {
            const decrypted = await decryptSignaling(row.candidate, sharedSecretRef.current);
            const candidate = JSON.parse(decrypted);
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch { /* skip bad candidate */ }
        }
      }

      // Create answer with Opus HD voice optimization
      const rawAnswer = await pc.createAnswer();
      const answer = preferOpusCodec(rawAnswer);
      await pc.setLocalDescription(answer);

      // Encrypt and store answer SDP
      const encryptedAnswer = await encryptSignaling(
        JSON.stringify(answer),
        sharedSecretRef.current
      );

      await supabase
        .from('elefa_calls')
        .update({
          answer_sdp: encryptedAnswer,
          status: 'connecting',
          answered_at: new Date().toISOString(),
        })
        .eq('id', callId);
    } catch (err: unknown) {
      const { message: errMsg, stack } = errMessage(err);
      log.error('[ElefaCall] answerCall failed:', errMsg, stack);
      if (errMsg.includes('getUserMedia') || errMsg.includes('NotAllowed') || errMsg.includes('Permission')) {
        toast.error('Microphone access denied. Please allow microphone permissions.');
      } else if (errMsg.includes('encrypt') || errMsg.includes('E2EE')) {
        toast.error('Encryption setup failed. Cannot answer this call.');
      } else if (errMsg.includes('network') || errMsg.includes('fetch')) {
        toast.error('Network error. Check your connection and try again.');
      } else {
        toast.error(`Failed to answer call: ${errMsg.length > 80 ? errMsg.slice(0, 80) + '…' : errMsg}`);
      }
      // Clean up on failure
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
      setState(IDLE_STATE);
    }
  }, [getUserMedia, createPeerConnection, setupIceTrickle]);

  // ─── Reject call ───────────────────────────────────────────────────────

  const rejectCall = useCallback(async (callId: string) => {
    setIsRingtonePlaying(false);
    window.__elefaCallNotifDismiss?.();
    window.__elefaCallNotifDismiss = null;

    await supabase
      .from('elefa_calls')
      .update({ status: 'rejected', ended_at: new Date().toISOString() })
      .eq('id', callId);

    // Insert rejected call event - callee inserts for rejected calls
    const currentState = stateRef.current;
    const { data: { session: sess } } = await supabase.auth.getSession();
    const myId = sess?.user?.id;
    if (myId && currentState.callerId && currentState.calleeId) {
      await insertCallEventMessage(myId, currentState.callerId, currentState.calleeId, {
        call_id: callId,
        is_video: currentState.isVideo,
        is_missed: true,
        duration: 0,
        status: 'rejected',
      });
    }

    // Cleanup ICE channel if any
    if (iceChannelRef.current) {
      supabase.removeChannel(iceChannelRef.current);
      iceChannelRef.current = null;
    }

    setState(IDLE_STATE);
  }, []);

  // ─── End call ──────────────────────────────────────────────────────────

  const endCall = useCallback(async () => {
    await endCallInternal();
  }, [endCallInternal]);

  // ─── Toggle mute / video ──────────────────────────────────────────────

  const toggleMute = useCallback(() => {
    const stream = stateRef.current.localStream;
    if (stream) {
      stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(prev => !prev);
    }
  }, []);

  const toggleVideo = useCallback(() => {
    const stream = stateRef.current.localStream;
    if (stream) {
      stream.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsVideoEnabled(prev => !prev);
    }
  }, []);

  // ─── Screen sharing (desktop) ─────────────────────────────────────────

  const toggleScreenShare = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;

    if (isScreenSharing) {
      // Revert to camera
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender && originalVideoTrackRef.current) {
        await sender.replaceTrack(originalVideoTrackRef.current);
      }
      screenTrackRef.current?.stop();
      screenTrackRef.current = null;
      setIsScreenSharing(false);
      return;
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const screenTrack = screenStream.getVideoTracks()[0];
      screenTrackRef.current = screenTrack;

      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        originalVideoTrackRef.current = sender.track;
        await sender.replaceTrack(screenTrack);
      }

      setIsScreenSharing(true);

      // Auto-revert when user stops sharing via browser UI
      screenTrack.onended = () => {
        if (sender && originalVideoTrackRef.current) {
          sender.replaceTrack(originalVideoTrackRef.current);
        }
        screenTrackRef.current = null;
        setIsScreenSharing(false);
      };
    } catch (err) {
      log.warn('[ElefaCall] Screen share cancelled or failed:', err);
    }
  }, [isScreenSharing]);

  // ─── Listen for incoming calls ────────────────────────────────────────

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setup = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Clean up stale ringing calls older than 2 minutes (prevents blocking new incoming)
      try {
        const twoMinAgo = new Date(Date.now() - 2 * 60_000).toISOString();
        await supabase
          .from('elefa_calls')
          .update({ status: 'missed', ended_at: new Date().toISOString(), end_reason: 'stale_cleanup' })
          .or(`caller_id.eq.${user.id},callee_id.eq.${user.id}`)
          .eq('status', 'ringing')
          .lt('created_at', twoMinAgo);
        log.debug('[ElefaCall] Stale call cleanup completed');
      } catch (err) {
        log.warn('[ElefaCall] Stale call cleanup skipped:', err);
      }

      // Gap 9: Use managed channel for auto-reconnection on network drop / lock
      try {
        const managed = createManagedChannel(
          'incoming-calls',
          (ch) => ch.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'elefa_calls',
            filter: `callee_id=eq.${user.id}`,
          },
          async (payload) => {
            const call = payload.new as ElefaCallRow;

            if (call.status === 'ringing' && stateRef.current.status === 'idle') {
              const { data: callerProfile } = await supabase
                .from('profiles')
                .select('full_name, avatar_url')
                .eq('user_id', call.caller_id)
                .single();

              const callerName = callerProfile?.full_name || 'Unknown';

              setState({
                callId: call.id,
                callerId: call.caller_id,
                calleeId: user.id,
                callerName,
                callerAvatar: callerProfile?.avatar_url || null,
                status: 'ringing',
                isVideo: call.is_video,
                isIncoming: true,
                duration: 0,
                localStream: null,
                remoteStream: null,
              });

              setIsRingtonePlaying(true);
              
              // Show native/web notification for background alerting
              // Pass answer/reject callbacks so notification click answers the call
              const notif = showIncomingCallNotification(
                callerName,
                call.is_video,
                () => {
                  // onAnswer - triggered when user clicks the notification
                  if (stateRef.current.callId) {
                    answerCall(stateRef.current.callId);
                  }
                },
                () => {
                  // onReject - currently not triggered by notification close
                  if (stateRef.current.callId) {
                    rejectCall(stateRef.current.callId);
                  }
                }
              );
              window.__elefaCallNotifDismiss = notif.dismiss;
            }
          }
          ),
        );

        unsubscribe = managed.unsubscribe;
      } catch (err) {
        log.warn('[ElefaCall] Incoming realtime setup failed; polling fallback remains active:', err);
      }

      // ── Polling fallback: catch calls missed during Realtime reconnect gaps ──
      const incomingPollTimer = setInterval(async () => {
        // Only poll when idle (not already in a call)
        if (stateRef.current.status !== 'idle') return;

        try {
          const { data: ringingCalls } = await supabase
            .from('elefa_calls')
            .select('*')
            .eq('callee_id', user.id)
            .eq('status', 'ringing')
            .gt('created_at', new Date(Date.now() - 60_000).toISOString())
            .order('created_at', { ascending: false })
            .limit(1);

          const call = ringingCalls?.[0] as ElefaCallRow | undefined;
          if (!call) return;

          // Only handle if we haven't already handled this call
          if (stateRef.current.callId === call.id) return;

          const { data: callerProfile } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('user_id', call.caller_id)
            .single();

          const callerName = callerProfile?.full_name || 'Unknown';

          setState({
            callId: call.id,
            callerId: call.caller_id,
            calleeId: user.id,
            callerName,
            callerAvatar: callerProfile?.avatar_url || null,
            status: 'ringing',
            isVideo: call.is_video,
            isIncoming: true,
            duration: 0,
            localStream: null,
            remoteStream: null,
          });

          setIsRingtonePlaying(true);

          const notif = showIncomingCallNotification(
            callerName,
            call.is_video,
            () => { if (stateRef.current.callId) answerCall(stateRef.current.callId); },
            () => { if (stateRef.current.callId) rejectCall(stateRef.current.callId); }
          );
          window.__elefaCallNotifDismiss = notif.dismiss;
        } catch (err) {
          // Silently ignore polling errors
        }
      }, 3000);

      // Store cleanup for polling
      const originalUnsubscribe = unsubscribe;
      unsubscribe = () => {
        clearInterval(incomingPollTimer);
        originalUnsubscribe?.();
      };
    };

    setup().catch((err) => {
      log.warn('[ElefaCall] Incoming call setup failed:', err);
    });
    return () => {
      unsubscribe?.();
    };
  // #2 FIX: Include answerCall/rejectCall in deps to prevent stale closures
  }, [answerCall, rejectCall]);

  // ─── Listen for call updates (answer, end, reject) ────────────────────

  useEffect(() => {
    if (!state.callId) return;

    // Process answer SDP (shared between Realtime and polling fallback)
    const processAnswerSdp = async (answerSdp: string) => {
      try {
        const decryptedAnswer = await decryptSignaling(
          answerSdp,
          sharedSecretRef.current
        );
        const answer = JSON.parse(decryptedAnswer);
        const pc = peerConnectionRef.current;
        if (pc && pc.signalingState !== 'closed' && !pc.remoteDescription) {
          await pc.setRemoteDescription(new RTCSessionDescription(answer));
          setState(prev => ({ ...prev, status: 'connecting' }));
          setIsRingtonePlaying(false);
        }

        // Load any early ICE candidates from the callee
        const { data: { session: sess } } = await supabase.auth.getSession();
        if (sess?.user) {
          const user = sess.user;
          const { data: earlyCandidates } = await supabase
            .from('elefa_call_ice_candidates')
            .select('candidate, sender_id')
            .eq('call_id', stateRef.current.callId!)
            .neq('sender_id', user.id);

          if (earlyCandidates && pc) {
            for (const row of earlyCandidates) {
              try {
                const decrypted = await decryptSignaling(
                  row.candidate,
                  sharedSecretRef.current
                );
                const candidate = JSON.parse(decrypted);
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch { /* skip */ }
            }
          }
        }
      } catch (err) {
        log.error('[ElefaCall] Failed to process answer:', err);
      }
    };

    // Use managed channel for auto-reconnection
    const { unsubscribe } = createManagedChannel(
      `call-${state.callId}`,
      (ch) => ch.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'elefa_calls',
          filter: `id=eq.${state.callId}`,
        },
        async (payload) => {
          const call = payload.new as ElefaCallRow;

          if (call.status === 'ended' || call.status === 'rejected' || call.status === 'missed') {
            await endCallInternal();
          } else if (call.status === 'connecting' && !stateRef.current.isIncoming && call.answer_sdp) {
            await processAnswerSdp(call.answer_sdp as string);
          }
        }
      ),
    );

    // Polling fallback for live calls - catches missed Realtime terminal events
    // after mobile background/foreground or a transient Realtime reconnect.
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const isPollableLiveState = () => stateRef.current.status === 'ringing' || stateRef.current.status === 'connected';
    if (stateRef.current.callId && isPollableLiveState()) {
      pollTimer = setInterval(async () => {
        // Stop polling if status changed
        if (!isPollableLiveState() || !stateRef.current.callId) {
          if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
          return;
        }
        try {
          const { data: call } = await supabase
            .from('elefa_calls')
            .select('status, answer_sdp')
            .eq('id', stateRef.current.callId)
            .single();

          if (!call) return;

          if (call.status === 'ended' || call.status === 'rejected' || call.status === 'missed') {
            if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
            await endCallInternal();
          } else if (call.answer_sdp && call.status === 'connecting') {
            if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
            await processAnswerSdp(call.answer_sdp);
          }
        } catch { /* ignore polling errors */ }
      }, 2000);
    }

    return () => {
      unsubscribe();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [state.callId, endCallInternal]);

  // ─── Auto-timeout ringing calls (60s → missed) ────────────────────────

  useEffect(() => {
    if (state.status !== 'ringing') return;

    const timeout = setTimeout(async () => {
      if (stateRef.current.callId) {
        await supabase
          .from('elefa_calls')
          .update({ status: 'missed', ended_at: new Date().toISOString() })
          .eq('id', stateRef.current.callId);

        // Insert missed call event - callee inserts for missed calls
        const currentState = stateRef.current;
        const { data: { session: sess } } = await supabase.auth.getSession();
        const myId = sess?.user?.id;
        if (myId && currentState.callerId && currentState.calleeId && currentState.isIncoming) {
          await insertCallEventMessage(myId, currentState.callerId, currentState.calleeId, {
            call_id: currentState.callId!,
            is_video: currentState.isVideo,
            is_missed: true,
            duration: 0,
            status: 'missed',
          });
        }

        // Show missed call notification
        showMissedCallNotification(
          stateRef.current.callerName || 'Unknown',
          stateRef.current.isVideo
        );
        
        window.__elefaCallNotifDismiss?.();
        window.__elefaCallNotifDismiss = null;

        await endCallInternal();
      }
    }, 60000);

    return () => clearTimeout(timeout);
  }, [state.status, state.callId, endCallInternal]);

  // ─── SECURITY: Real-time membership & key change enforcement during calls ──
  useEffect(() => {
    if (state.status === 'idle' || !state.callId) return;
    const otherUserId = state.isIncoming ? state.callerId : state.calleeId;
    if (!otherUserId) return;

    // Terminate call if removed from group mid-call
    const memberChannel = supabase
      .channel(`call-member-${state.callId}`)
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'chat_room_members' },
        (payload) => {
          const deleted = payload.old as { user_id?: string };
          const myId = stateRef.current.isIncoming ? stateRef.current.calleeId : stateRef.current.callerId;
          if (deleted.user_id === myId) {
            log.warn('[ElefaCall] SECURITY: Removed from group during call - terminating');
            toast.error('You were removed from the group');
            endCallInternal('Removed from group');
          }
        }
      )
      .subscribe();

    // Terminate call if other party's identity key changes
    const keyChannel = supabase
      .channel(`call-key-${otherUserId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'encryption_keys', filter: `user_id=eq.${otherUserId}` },
        () => {
          log.error('[ElefaCall] SECURITY: Remote identity key changed during call - terminating');
          toast.error('Security alert: Contact identity changed');
          endCallInternal('Identity key changed mid-call');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(memberChannel);
      supabase.removeChannel(keyChannel);
    };
  }, [state.status, state.callId, state.callerId, state.calleeId, state.isIncoming, endCallInternal]);

  // ─── beforeunload: release camera on page close ──────────────────────
  useEffect(() => {
    const cleanup = () => {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      remoteStreamRef.current?.getTracks().forEach(t => t.stop());
      originalVideoTrackRef.current?.stop();
      screenTrackRef.current?.stop();
    };
    window.addEventListener('beforeunload', cleanup);
    return () => window.removeEventListener('beforeunload', cleanup);
  }, []);

  // ─── Start quality monitor when connected ──────────────────────────────

  useEffect(() => {
    if (state.status === 'connected' && peerConnectionRef.current) {
      const monitor = new CallQualityMonitor(
        peerConnectionRef.current,
        (stats: CallQualityStats) => setCallQuality(stats.quality),
        {
          onPoorConnection: () => toast.warning('Poor connection - audio quality may be affected'),
          adaptiveBitrate: true,
        }
      );
      monitor.start();
      qualityMonitorRef.current = monitor;
      return () => monitor.stop();
    }
  }, [state.status]);

  // ─── ICE restart on network change ────────────────────────────────────

  useEffect(() => {
    if (state.status !== 'connected') return;

    const handleNetworkChange = () => {
      const pc = peerConnectionRef.current;
      if (!pc || pc.connectionState === 'closed') return;
      log.debug('[ElefaCall] Network change - restarting ICE');
      setIsReconnecting(true);
      try { pc.restartIce(); } catch { /* ignore */ }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const pc = peerConnectionRef.current;
        if (pc && (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed')) {
          log.debug('[ElefaCall] Returned to foreground - restarting ICE');
          setIsReconnecting(true);
          try { pc.restartIce(); } catch { /* ignore */ }
        }
      }
    };

    const conn = navigator.connection;
    conn?.addEventListener('change', handleNetworkChange);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      conn?.removeEventListener('change', handleNetworkChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [state.status]);

  // ─── Speaker toggle ─────────────────────────────────────────────────
  // Proper audio routing via setSinkId with device enumeration fallback
  const toggleSpeaker = useCallback(async () => {
    const newState = !isSpeakerOn;
    setIsSpeakerOn(newState);

    // Find all audio/video elements used for remote playback
    const audioEls = Array.from(document.querySelectorAll('audio[autoplay]')) as HTMLAudioElement[];
    const videoEls = Array.from(document.querySelectorAll('video[autoplay]')) as HTMLVideoElement[];
    const elements: HTMLMediaElement[] = [...audioEls, ...videoEls];

    // Try setSinkId for true speaker routing
    const supportsSinkId = elements.length > 0 && 'setSinkId' in elements[0];

    if (supportsSinkId) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

        let targetDeviceId = '';
        if (newState) {
          // Find speakerphone device
          const speaker = audioOutputs.find(d =>
            d.label.toLowerCase().includes('speaker') &&
            !d.label.toLowerCase().includes('earpiece')
          );
          targetDeviceId = speaker?.deviceId || 'default';
        } else {
          // Find earpiece or use default (communications device)
          const earpiece = audioOutputs.find(d =>
            d.label.toLowerCase().includes('earpiece') ||
            d.label.toLowerCase().includes('handset')
          );
          // 'communications' is the earpiece on mobile Chrome; fallback to default
          targetDeviceId = earpiece?.deviceId || 'communications';
        }

        for (const el of elements) {
          try {
            await (el as SinkIdAudio).setSinkId?.(targetDeviceId);
          } catch {
            // setSinkId may fail for 'communications' - fallback to default
            try { await (el as SinkIdAudio).setSinkId?.('default'); } catch {}
          }
        }
        log.debug(`[ElefaCall] Speaker ${newState ? 'ON' : 'OFF'} via setSinkId (${targetDeviceId})`);
        return;
      } catch (err) {
        log.warn('[ElefaCall] setSinkId failed, falling back to volume:', err);
      }
    }

    // Fallback: volume-based approach
    const vol = newState ? 1.0 : 0.4;
    for (const el of elements) {
      el.volume = vol;
    }
    log.debug(`[ElefaCall] Speaker ${newState ? 'ON' : 'OFF'} via volume (${vol})`);
  }, [isSpeakerOn]);

  // ─── Flip camera (front/rear) ──────────────────────────────────────
  // Must be called from user gesture for mobile browser security
  const flipCamera = useCallback(async () => {
    const pc = peerConnectionRef.current;
    const stream = localStreamRef.current;
    if (!pc || !stream) return;

    const currentVideoTrack = stream.getVideoTracks()[0];
    if (!currentVideoTrack) return;

    // Determine current facing mode
    const settings = currentVideoTrack.getSettings();
    const currentFacing = settings.facingMode || 'user';
    const newFacing = currentFacing === 'user' ? 'environment' : 'user';

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: newFacing },
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30, max: 30 },
        },
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!newVideoTrack) return;

      // Replace track on peer connection sender
      const sender = pc.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(newVideoTrack);
      }

      // Replace track in local stream
      stream.removeTrack(currentVideoTrack);
      stream.addTrack(newVideoTrack);
      currentVideoTrack.stop();

      // Update local stream ref + state to trigger re-render
      localStreamRef.current = stream;
      setState(prev => ({ ...prev, localStream: new MediaStream(stream.getTracks()) }));

      log.debug(`[ElefaCall] Camera flipped to ${newFacing}`);
    } catch (err) {
      log.warn('[ElefaCall] Flip camera failed:', err);
      // Try without 'exact' constraint as fallback
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: newFacing },
        });
        const fallbackTrack = fallbackStream.getVideoTracks()[0];
        if (fallbackTrack) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(fallbackTrack);
          stream.removeTrack(currentVideoTrack);
          stream.addTrack(fallbackTrack);
          currentVideoTrack.stop();
          localStreamRef.current = stream;
          setState(prev => ({ ...prev, localStream: new MediaStream(stream.getTracks()) }));
          log.debug(`[ElefaCall] Camera flipped to ${newFacing} (fallback)`);
        }
      } catch (fallbackErr) {
        log.error('[ElefaCall] Flip camera fallback also failed:', fallbackErr);
        toast.error('Could not switch camera');
      }
    }
  }, []);

  // ─── Hold / Resume (CallKit-compatible: CXSetHeldCallAction) ──────
  const toggleHold = useCallback(() => {
    const pc = peerConnectionRef.current;
    const localStream = localStreamRef.current;
    if (!pc || !localStream) return;

    const newHoldState = !isOnHold;
    setIsOnHold(newHoldState);

    // Disable/enable all local tracks (audio + video)
    localStream.getTracks().forEach(track => {
      track.enabled = !newHoldState;
    });

    // Signal hold/resume to remote peer via Realtime broadcast
    const callId = stateRef.current.callId;
    if (callId) {
      const holdChannel = supabase.channel(`hold-${callId}`);
      holdChannel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          holdChannel.send({
            type: 'broadcast',
            event: 'hold_state',
            payload: { isOnHold: newHoldState },
          });
          // Unsubscribe after sending
          setTimeout(() => supabase.removeChannel(holdChannel), 1000);
        }
      });
    }

    log.debug(`[ElefaCall] Call ${newHoldState ? 'put on hold' : 'resumed'}`);
  }, [isOnHold]);

  // Listen for remote hold/resume signals
  useEffect(() => {
    const callId = state.callId;
    if (!callId || state.status !== 'connected') return;

    const channel = supabase
      .channel(`hold-listen-${callId}`)
      .on('broadcast', { event: 'hold_state' }, (payload) => {
        const remoteHold = payload.payload?.isOnHold ?? false;
        remoteHoldRef.current = remoteHold;
        setIsRemoteOnHold(remoteHold);
        log.debug(`[ElefaCall] Remote party ${remoteHold ? 'put call on hold' : 'resumed call'}`);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [state.callId, state.status]);

  // ─── Heartbeat keepalive (prevents auto-disconnect) ────────────────
  useEffect(() => {
    if (state.status !== 'connected' || !state.callId) return;

    const doHeartbeat = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId || !stateRef.current.callId) return;

      // #6 FIX: Proactive auth re-validation for long calls (2h+ JWT expiry)
      if (session?.expires_at) {
        const expiresIn = session.expires_at * 1000 - Date.now();
        if (expiresIn < 300_000) { // Within 5 minutes of expiry
          log.debug('[ElefaCall] JWT expiring soon, forcing refresh');
          await supabase.auth.refreshSession();
        }
      }

      const isCaller = stateRef.current.callerId === userId;
      const myField = isCaller ? 'caller_heartbeat' : 'callee_heartbeat';
      const peerField = isCaller ? 'callee_heartbeat' : 'caller_heartbeat';

      // Update my heartbeat
      await supabase
        .from('elefa_calls')
        .update({ [myField]: new Date().toISOString() } as Record<string, string>)
        .eq('id', stateRef.current.callId);

      // Check peer heartbeat
      const { data: call } = await supabase
        .from('elefa_calls')
        .select('caller_heartbeat, callee_heartbeat, status')
        .eq('id', stateRef.current.callId!)
        .single();

      if (!call || call.status === 'ended') return;

      const peerHb = (call as Record<string, unknown>)[peerField] as string | undefined;
      if (peerHb) {
        const staleness = Date.now() - new Date(peerHb).getTime();
        if (staleness > 90_000) {
          log.warn('[ElefaCall] Peer heartbeat stale (', Math.round(staleness / 1000), 's) - ending call');
          endCallInternal('Peer disconnected (heartbeat timeout)');
        }
      }
    };

    // Initial heartbeat
    doHeartbeat();
    heartbeatIntervalRef.current = setInterval(doHeartbeat, 30_000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [state.status, state.callId, endCallInternal]);

  return {
    ...state,
    initiateCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    isMuted,
    isVideoEnabled,
    callQuality,
    isReconnecting,
    isScreenSharing,
    toggleScreenShare,
    isSpeakerOn,
    toggleSpeaker,
    isOnHold,
    isRemoteOnHold,
    toggleHold,
    flipCamera,
  };
}
